import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { NUMBERS_ONLY } from 'validations/general';
import { useTranslation } from 'react-i18next';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import { Button, Input } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ListingCreationSelect from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelect';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import { ListingQueryFilterParams } from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';
import {
  createText,
  mergeRanges,
  RangeOptionsCopies,
  getRangesFromParams,
  getParamsFromRanges,
  translateInputToSelectValues,
  getSelectValueFromRanges,
} from './utils';
import { FIRST_PAGE, MAX_VALUE } from 'data/global';
import utils, {
  doesValueExist,
  getHasElementsInArray,
  getIsArrayEmpty,
  isSingleElementArray,
} from 'utils';
import { FilterActionWrapperProps } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper/FilterActionWrapper.component';
import useQueryFilter from 'hooks/useQueryFilter';

import './RangeFilterWrapper.styles.scss';

export const MAX_RANGE_DEFAULT = 50000000;

const EMPTY_STATE = { from: undefined, to: undefined } as PriceRangeValue;

export type PriceRangeValue = { from?: number | string; to?: number | string };

export type PriceRangeCustomApply = {
  value?: PriceRangeValue;
  onApply?: (value: PriceRangeValue) => void;
};

type RangeFilterWrapperProps = {
  className?: string;
  selectValues: {
    minValue: number;
    maxValue: number;
    copies: RangeOptionsCopies;
  };
  paramsKeywords: {
    fromKeyword: keyof ListingQueryFilterParams;
    toKeyword: keyof ListingQueryFilterParams;
  };
  triggerText: string;
  allRangesText: string;
  triggerIcon?: ReactNode;
  applyButtonText: string;
  maxRangeValue?: number;
  isEbidta?: boolean;
} & PriceRangeCustomApply &
  Partial<FilterActionWrapperProps>;

const RangeFilterWrapper: React.FC<RangeFilterWrapperProps> = (props) => {
  const {
    className,
    selectValues,
    paramsKeywords,
    triggerText,
    allRangesText,
    triggerIcon,
    applyButtonText,
    maxRangeValue = MAX_RANGE_DEFAULT,
    isEbidta,
    value,
    onApply,
    ...rest
  } = props;

  const { fromKeyword, toKeyword } = paramsKeywords;

  const {
    minValue: selectMinVal,
    maxValue: selectMaxVal,
    copies: selectCopies,
  } = selectValues;

  const { params, applyQueryFilter } = useQueryFilter();

  const classes = classNames('range-filter-wrapper', className);

  const [from, to] = value
    ? [value?.from, value?.to]
    : [params[fromKeyword], params[toKeyword]];

  const initialValue = useMemo(() => getRangesFromParams(from, to), [from, to]);

  const [showSelect, setShowSelect] = useState(true);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const [currentState, setCurrentState] = useState(initialValue);
  const [inputState, setInputState] = useState<PriceRangeValue>(EMPTY_STATE);

  const { t } = useTranslation();

  const SELECT_TO_INPUT = useMemo(
    () => ({
      [selectCopies.UNDER_MIN]: { from: 0, to: selectMinVal },
      [selectCopies.BETWEEN_VALUES]: { from: selectMinVal, to: selectMaxVal },
      [selectCopies.ABOVE_MAX]: { from: selectMaxVal, to: MAX_VALUE },
    }),
    [selectCopies, selectMaxVal, selectMinVal],
  );

  const selectOptions = useMemo(
    () =>
      Object.values(selectCopies).map((value) => ({
        value,
        label: value,
      })),
    [selectCopies],
  );

  const triggerTitle = useMemo(() => {
    if (getIsArrayEmpty(initialValue)) return triggerText;

    const merged = mergeRanges(initialValue);

    if (!merged[0].from && +merged[0].to === MAX_VALUE) return allRangesText;

    return merged.map(createText).join(', ');
  }, [initialValue, triggerText, allRangesText]);

  const translateInputToSelect = useCallback(
    (from?: number | string, to?: number | string) =>
      translateInputToSelectValues(
        from,
        to,
        selectMinVal,
        selectMaxVal,
        selectCopies,
        SELECT_TO_INPUT,
      ),
    [SELECT_TO_INPUT, selectCopies, selectMaxVal, selectMinVal],
  );

  const createInputState = useCallback(
    (from, to) => ({
      to: to ? (+to !== MAX_VALUE ? +to : undefined) : maxRangeValue,
      from: from ? +from : 0,
    }),
    [maxRangeValue],
  );

  const applyRangeFilter = useCallback(() => {
    const array = showSelect ? currentState : [inputState];

    const { from, to } = getParamsFromRanges(array);

    if (onApply) {
      onApply?.({ from, to });
    } else {
      applyQueryFilter(fromKeyword, from);
      applyQueryFilter(toKeyword, to);
      applyQueryFilter('page', FIRST_PAGE);
    }
    dropdown.setIsOpen(false);
  }, [
    dropdown,
    currentState,
    showSelect,
    inputState,
    fromKeyword,
    toKeyword,
    onApply,
    applyQueryFilter,
  ]);

  const onSelectFilterChange = useCallback(
    (values: string[]) => {
      const ranges = Object.entries(SELECT_TO_INPUT)
        .filter(([key, _]) => values.includes(key))
        .map(([_, value]) => value);

      setCurrentState(ranges);
    },
    [SELECT_TO_INPUT],
  );

  const onChangeSelectView = useCallback(
    (value: boolean) => {
      if (!value) {
        if (isSingleElementArray(currentState))
          setInputState(
            createInputState(currentState[0]?.from, currentState[0]?.to),
          );
        else
          setInputState((old) =>
            getHasElementsInArray(currentState) ? EMPTY_STATE : old,
          );
      } else
        setCurrentState((old) =>
          inputState?.to
            ? translateInputToSelect(inputState?.from, inputState?.to)
            : old,
        );

      setShowSelect(value);
    },
    [currentState, inputState, createInputState, translateInputToSelect],
  );

  const isEmpty = useMemo(
    () =>
      showSelect
        ? getIsArrayEmpty(currentState)
        : !doesValueExist(inputState?.from) || !doesValueExist(inputState?.to),
    [currentState, inputState, showSelect],
  );

  const error = useMemo(() => {
    const { from, to } = inputState;

    return (from && +from !== 0 && !NUMBERS_ONLY.test(String(from))) ||
      (to && +to !== 0 && !NUMBERS_ONLY.test(String(to)))
      ? t('priceValuesMustBeNumber')
      : +from >= +to
      ? t('minValErrorText')
      : '';
  }, [inputState, t]);

  useEffect(() => {
    setCurrentState(initialValue);
    setInputState(createInputState(initialValue[0]?.from, initialValue[0]?.to));
  }, [initialValue, createInputState]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      portalClassName="range-filter-wrapper__portal"
      icon={triggerIcon}
      trigerTitle={triggerTitle}
      {...rest}
    >
      <div className="range-filter-wrapper__content-container">
        <div
          className={classNames(
            'range-filter-wrapper__content-container__selection-cards',
            {
              shown: showSelect,
            },
          )}
        >
          <div className="range-filter-wrapper__content-container__heading">
            <p>{t('selectARange')}</p>
            <Button
              onClick={() => onChangeSelectView(false)}
              className="as-link"
            >
              {t('chooseSpecificRange')}
              <ArrowrightIcon />
            </Button>
          </div>

          <ListingCreationSelect
            value={getSelectValueFromRanges(currentState, SELECT_TO_INPUT)}
            columns={3}
            options={selectOptions}
            onChange={onSelectFilterChange}
            styleType="secondary"
          />
        </div>
        <div
          className={classNames(
            'range-filter-wrapper__content-container__inputs',
            {
              shown: !showSelect,
              error,
            },
          )}
        >
          <div className="range-filter-wrapper__content-container__heading">
            <p>{t('chooseSpecificRange')}</p>
            <Button
              onClick={() => onChangeSelectView(true)}
              className="as-link"
            >
              <ArrowleftIcon />
              {t('selectARange')}
            </Button>
          </div>
          <div className="range-filter-wrapper__content-container__inputs__wrapper">
            <div>
              <label>{t(isEbidta ? 'minAmount' : 'minPrice')}</label>
              <Input
                prefixNode="$"
                placeholder="0"
                value={utils.getDotedNumber(inputState?.from)}
                onChange={(ev) =>
                  setInputState((old) => ({
                    ...old,
                    from: utils.getIntegerFromDottedNumber(
                      ev.target.value.replace(/\D/, ''),
                    ),
                  }))
                }
              />
            </div>
            <div>
              <label>{t(isEbidta ? 'maxAmount' : 'maxPrice')}</label>

              <Input
                prefixNode="$"
                placeholder={t('noMax')}
                value={
                  isEbidta &&
                  (+inputState.to >= maxRangeValue || +inputState.to === 0)
                    ? ''
                    : utils.getDotedNumber(inputState.to)
                }
                onChange={(ev) => {
                  if (+ev.target.value === 0 || !ev.target.value) {
                    setInputState((old) => ({
                      ...old,
                      to: MAX_RANGE_DEFAULT,
                    }));
                    return;
                  }

                  ev.target.value &&
                    setInputState((old) => ({
                      ...old,
                      to: utils.getIntegerFromDottedNumber(
                        ev.target.value.replace(/\D/, ''),
                      ),
                    }));
                }}
              />
            </div>
          </div>
          {error && <small>{error}</small>}
        </div>
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={!!error || isEmpty}
          onClick={applyRangeFilter}
        >
          {applyButtonText}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default RangeFilterWrapper;
